import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronLeft, ChevronRight, Check, Palette, Settings, Cloud, CheckCircle, Loader2, Download, Upload, Smartphone } from 'lucide-react';
import Button from '../UI/Button';
import PackageNameInput from './PackageNameInput';
import AppIconUploader from './AppIconUploader';
import BrandColors from './BrandColors';
import MobilePreview from './MobilePreview';
import FirebaseConfigStep from './FirebaseConfigStep';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import { ThemeColorPalette } from '../../types';
import { DEFAULT_THEME_COLORS, mergeThemeColors } from '../../constants/themeColors';

export interface AppCreationFormData {
  // Step 1: Basic Information
  name: string;
  packageName: string;
  platform?: 'ios' | 'android' | 'both';
  title?: string;
  description?: string;
  appIcon?: File;
  stepsDone: string; // Added for edit mode support
  appIconUrl?: string; // For edit mode
  onboardBgUrl?: string; // For edit mode
  homeBgUrl?: string; // For edit mode
  onboardBg?: File; // Background image file for onboard screen
  homeBg?: File; // Background image file for home screen
  
  // Step 2: Theme
  themeColors?: ThemeColorPalette;
  
  // Step 3: Firebase Configuration
  sha1?: string;
  sha256?: string;
  googleServicesJson?: File;
  googleServicesPlist?: File;
  googleServicesJsonUrl?: string; // For edit mode
  googleServicesPlistUrl?: string; // For edit mode
  pipelineId?: string; // Pipeline ID field
  iosPipelineId?: string; // iOS Pipeline ID field
  googleClientApiAndroid?: string;
  googleClientApiIos?: string;
  mobileProvision?: File;
  mobileProvisionName?: string;
  mobileProvisionUUID?: string;
  
  // Keystore fields
  keystoreUrl?: string;
  keystorePassword?: string;
  keystoreAlias?: string;
  keystoreBuffer?: string; // Base64 encoded keystore buffer
  
  // Step 4: Build Status (handled by component)
  buildId?: string | null;
  buildStatus?: 'pending' | 'running' | 'completed' | 'failed';
  iosBuildId?: string | null;
  iosBuildStatus?: 'pending' | 'running' | 'completed' | 'failed';

  // Version fields
  androidVersionCode?: string;
  androidVersionName?: string;
  iosVersionCode?: string;
}

// Extended interface for form compatibility
export interface AppCreationFormDataExtended extends AppCreationFormData {
  onboardBg?: File;
  homeBg?: File;
}

interface AppCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AppCreationFormData) => Promise<void>;
  onSaveDraft?: (data: AppCreationFormData) => Promise<void>;
  isLoading?: boolean;
  isEditMode?: boolean;
  editData?: Partial<AppCreationFormData>;
  editId?: string; // Flavour ID for edit mode
}

const steps = [
  { id: 1, title: 'Basic Information', icon: Settings, description: 'App details and identification' },
  { id: 2, title: 'Theme & Preview', icon: Palette, description: 'Customize appearance and preview' },
  { id: 3, title: 'Firebase Config', icon: Cloud, description: 'Configure Firebase and certificates' },
  { id: 4, title: 'Review & Create', icon: CheckCircle, description: 'Review configuration and create app' },
];

const AppCreationWizard: React.FC<AppCreationWizardProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSaveDraft,
  isLoading = false,
  isEditMode = false,
  editData,
  editId,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [packageNameStatus, setPackageNameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [packageNameError, setPackageNameError] = useState<string | null>(null);
  const [appIconPreview, setAppIconPreview] = useState<string | null>(null);
  const [brandColors, setBrandColors] = useState<ThemeColorPalette>({});
  const [homeBgPreview, setHomeBgPreview] = useState<string | null>(null);
  const [onboardBgPreview, setOnboardBgPreview] = useState<string | null>(null);
  const [googleServicesJsonPreview, setGoogleServicesJsonPreview] = useState<string | null>(null);
  const [googleServicesPlistPreview, setGoogleServicesPlistPreview] = useState<string | null>(null);
  const [buildId, setBuildId] = useState<string | null>(null);
  const [buildStatus, setBuildStatus] = useState<'pending' | 'running' | 'completed' | 'failed'>('pending');
  const [keystoreData, setKeystoreData] = useState<{
    keystoreSha1?: string;
    keystoreSha256?: string;
    keystoreBuffer?: string; // Base64 buffer
    keystorePassword?: string;
    keystoreAlias?: string;
  } | null>(null);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState<AppCreationFormDataExtended | null>(null);
  const [originalPackageName, setOriginalPackageName] = useState<string | null>(null);
  const [isFormPopulated, setIsFormPopulated] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCheckingIosBuild, setIsCheckingIosBuild] = useState(false);
  const [buildMode, setBuildMode] = useState<'android-build-only' | 'android-build-upload' | 'ios-build-only'>('android-build-only');
  const [isParsingProvisionProfile, setIsParsingProvisionProfile] = useState(false);

  // Version states — DB values (for comparison/validation)
  // In create mode these default to '0'/'0.0' so the textbox defaults ('1'/'1.0') are already valid.
  // In edit mode these are populated from the actual DB values.
  const [dbAndroidVersionCode, setDbAndroidVersionCode] = useState<string>('0');
  const [dbAndroidVersionName, setDbAndroidVersionName] = useState<string>('0.0');
  const [dbIosVersionCode, setDbIosVersionCode] = useState<string>('0.0');
  // Version states — current textbox values
  const [androidVersionCode, setAndroidVersionCode] = useState<string>('1');
  const [androidVersionName, setAndroidVersionName] = useState<string>('1.0');
  const [iosVersionCode, setIosVersionCode] = useState<string>('1.0');
  // Version field errors
  const [versionErrors, setVersionErrors] = useState<{
    androidVersionCode?: string;
    androidVersionName?: string;
    iosVersionCode?: string;
  }>({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
    reset,
    getValues,
  } = useForm<AppCreationFormDataExtended>({
    defaultValues: {
      platform: 'both',
    },
    mode: 'onChange', // Enable real-time validation
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form to default values
      reset({
        platform: 'both',
        name: '',
        packageName: '',
        title: '',
        description: '',
        themeColors: {},
        sha1: '',
        sha256: '',
        stepsDone: '1',
        pipelineId: '',
        iosPipelineId: '',
        mobileProvision: undefined,
        mobileProvisionName: '',
        mobileProvisionUUID: '',
        iosBuildId: null,
        iosBuildStatus: undefined,
      });
      
      // Reset all component states
      setIsFormPopulated(false);
      setCurrentStep(1);
      setCompletedSteps([]);
      setPackageNameStatus('idle');
      setPackageNameError(null);
      setHasChanges(false);
      setOriginalData(null);
      setOriginalPackageName(null);
      setAppIconPreview(null);
      setOnboardBgPreview(null);
      setHomeBgPreview(null);
      setGoogleServicesJsonPreview(null);
      setGoogleServicesPlistPreview(null);
      setBrandColors({});
      setKeystoreData(null);
      setBuildId(null);
      setBuildStatus('pending');
      // Reset version states — DB baseline back to create-mode defaults
      setDbAndroidVersionCode('0');
      setDbAndroidVersionName('0.0');
      setDbIosVersionCode('0.0');
      setAndroidVersionCode('1');
      setAndroidVersionName('1.0');
      setIosVersionCode('1.0');
      setVersionErrors({});
      setValue('iosPipelineId', '');
      setValue('iosBuildId', null);
      setValue('iosBuildStatus', undefined);
      setIsDraftSaving(false);
    }
  }, [isOpen, reset]);

  // Populate form with edit data when in edit mode
  useEffect(() => {
    if (isEditMode && editData && isOpen && !isFormPopulated) {
      const formData: AppCreationFormDataExtended = {
        platform: editData.platform || 'both', // Use platform from API data
        name: editData.name || '',
        packageName: editData.packageName || '',
        title: editData.title || '',
        description: editData.description || '',
        themeColors: editData.themeColors || {},
        sha1: editData.sha1 || '',
        sha256: editData.sha256 || '',
        stepsDone: editData.stepsDone || '1',
        // Include pipelineId from edit data
        pipelineId: editData.pipelineId || '',
        iosPipelineId: editData.iosPipelineId || '',
        iosBuildId: editData.iosBuildId ?? null,
        iosBuildStatus: editData.iosBuildStatus || 'pending',
        // Include image URL fields for proper change detection
        appIconUrl: editData.appIconUrl,
        onboardBgUrl: editData.onboardBgUrl,
        homeBgUrl: editData.homeBgUrl,
        // Include Google Services file fields for change detection
        googleServicesJsonUrl: editData.googleServicesJsonUrl,
        googleServicesPlistUrl: editData.googleServicesPlistUrl,
        mobileProvisionName: editData.mobileProvisionName || '',
        mobileProvisionUUID: editData.mobileProvisionUUID || '',
      };
      setCurrentStep(Number.parseInt(formData.stepsDone));
      setCompletedSteps(
        Array.from({ length: Number.parseInt(formData.stepsDone) }, (_, i) => i + 1)
      );
      // Store original data for change detection - use the raw editData, not processed formData
      setOriginalData(editData as AppCreationFormDataExtended);
      
      // Store original field values for locking logic
      setOriginalPackageName(editData.packageName || null);

      // Store original image URLs for change detection
      if (editData.appIconUrl) {
        setAppIconPreview(editData.appIconUrl);
      }
      if (editData.onboardBgUrl) {
        setOnboardBgPreview(editData.onboardBgUrl);
      }
      if (editData.homeBgUrl) {
        setHomeBgPreview(editData.homeBgUrl);
      }
      if (editData.googleServicesJsonUrl) {
        setGoogleServicesJsonPreview(editData.googleServicesJsonUrl);
      }
      if (editData.googleServicesPlistUrl) {
        setGoogleServicesPlistPreview(editData.googleServicesPlistUrl);
      }
      
      // Store original theme colors for change detection
      if (editData.themeColors) {
        setBrandColors(editData.themeColors);
      }
      
      // Reset change state
      setHasChanges(false);
      
      // Reset form first, then populate with edit data
      reset(formData);
      setBuildId(editData.buildId ?? null);
      setBuildStatus(
        (editData.buildStatus as 'pending' | 'running' | 'completed' | 'failed' | undefined) ||
          'pending',
      );
      setValue('iosPipelineId', editData.iosPipelineId || '');
      setValue('iosBuildId', editData.iosBuildId ?? null);
      setValue('iosBuildStatus', editData.iosBuildStatus ?? undefined);

      // Populate version states from DB values and auto-increment for current textbox values
      const dbVerCode = editData.androidVersionCode || '1';
      const dbVerName = editData.androidVersionName || '1.0';
      const dbIosVerCode = editData.iosVersionCode || '1.0';
      setDbAndroidVersionCode(dbVerCode);
      setDbAndroidVersionName(dbVerName);
      setDbIosVersionCode(dbIosVerCode);
      setAndroidVersionCode(incrementVersion(dbVerCode));
      setAndroidVersionName(dbVerName);
      setIosVersionCode(dbIosVerCode);
      setVersionErrors({});

      // Mark form as populated
      setIsFormPopulated(true);
      
      // Set form population time to prevent false positive change detection
      (window as any).formPopulationTime = Date.now();
      
      // Reset change state after a short delay to ensure form is fully populated
      setTimeout(() => {
        setHasChanges(false);
      }, 100);
      

    }
  }, [isEditMode, editData, isOpen, reset, isFormPopulated]);

  // Simple change detection - compare current form data with original data
  useEffect(() => {
    if (!isEditMode || !originalData) {
      // Create mode: check if any meaningful data has been entered
      // Skip change detection for a short time after form opens to avoid false positives
      if (isOpen && Date.now() - (window as any).formOpenTime < 1000) {
        return;
      }
      
      const currentData = getValues();
      
      // Only consider it a change if user has actually entered meaningful data
      const hasFormData = 
        (currentData.name && currentData.name.trim() !== '') ||
        (currentData.packageName && currentData.packageName.trim() !== '') ||
        (currentData.description && currentData.description.trim() !== '') ||
        (currentData.title && currentData.title.trim() !== '');
      
      const hasFileData = 
        currentData.appIcon || 
        currentData.onboardBg || 
        currentData.homeBg ||
        currentData.googleServicesJson ||
        currentData.googleServicesPlist ||
        appIconPreview ||
        onboardBgPreview ||
        homeBgPreview ||
        googleServicesJsonPreview ||
        googleServicesPlistPreview;
      
      // Only consider theme data as changes if user has actually modified colors
      const hasThemeData =
        brandColors &&
        Object.keys(brandColors).length > 0 &&
        Object.keys(brandColors).some((key) => {
          const typedKey = key as keyof ThemeColorPalette;
          return brandColors[typedKey] !== DEFAULT_THEME_COLORS[typedKey];
        });
      
      setHasChanges(Boolean(hasFormData || hasFileData || hasThemeData));
      return;
    }

    // Skip change detection for a short time after form population to avoid false positives
    if (isFormPopulated && Date.now() - (window as any).formPopulationTime < 500) {
      return;
    }

    // Edit mode: robust comparison with original data
    const currentData = getValues();
    
    // Helper function to compare values properly
    const compareValues = (current: any, original: any): boolean => {
      // Handle null/undefined cases
      if (current === original) return true;
      if (current == null && original == null) return true;
      if (current == null || original == null) return false;
      
      // Handle strings - trim and compare
      if (typeof current === 'string' && typeof original === 'string') {
        return current.trim() === original.trim();
      }
      
      // Handle objects - deep comparison
      if (typeof current === 'object' && typeof original === 'object') {
        return JSON.stringify(current) === JSON.stringify(original);
      }
      
      // Handle other types
      return current === original;
    };
    
    const hasChanges = 
      !compareValues(currentData.name, originalData.name) ||
      !compareValues(currentData.packageName, originalData.packageName) ||
      !compareValues(currentData.platform, originalData.platform) ||
      !compareValues(currentData.description, originalData.description) ||
      !compareValues(currentData.title, originalData.title) ||
      !compareValues(currentData.appIcon, originalData.appIcon) ||
      !compareValues(currentData.onboardBg, originalData.onboardBg) ||
      !compareValues(currentData.homeBg, originalData.homeBg) ||
      !compareValues(appIconPreview, originalData.appIconUrl) ||
      !compareValues(onboardBgPreview, originalData.onboardBgUrl) ||
      !compareValues(homeBgPreview, originalData.homeBgUrl) ||
      !compareValues(googleServicesJsonPreview, originalData.googleServicesJsonUrl) ||
      !compareValues(googleServicesPlistPreview, originalData.googleServicesPlistUrl) ||
      !compareValues(
        pickEditableColors(brandColors),
        pickEditableColors(originalData.themeColors),
      ) ||
      // Firebase Configuration fields
      !compareValues(currentData.sha1, originalData.sha1) ||
      !compareValues(currentData.sha256, originalData.sha256) ||
      !compareValues(currentData.googleServicesJson, originalData.googleServicesJson) ||
      !compareValues(currentData.googleServicesPlist, originalData.googleServicesPlist) ||
      !compareValues(currentData.googleServicesJsonUrl, originalData.googleServicesJsonUrl) ||
      !compareValues(currentData.googleServicesPlistUrl, originalData.googleServicesPlistUrl) ||
      !compareValues(currentData.pipelineId, originalData.pipelineId) ||
      !compareValues(currentData.iosPipelineId, originalData.iosPipelineId) ||
      // Keystore fields
      !compareValues(currentData.keystorePassword, originalData.keystorePassword) ||
      !compareValues(currentData.keystoreAlias, originalData.keystoreAlias) ||
      !compareValues(currentData.keystoreBuffer, originalData.keystoreBuffer);

    setHasChanges(hasChanges);
  }, [watch(), isEditMode, originalData, appIconPreview, onboardBgPreview, homeBgPreview, googleServicesJsonPreview, googleServicesPlistPreview, brandColors]);


  // Get theme colors with defaults (returns the FULL palette — all ~80 keys
  // from DEFAULT_THEME_COLORS, with PRIMARY/SECONDARY/ACCENT overridden by
  // brandColors). Used when constructing the payload sent to the backend.
  const getThemeColorsWithDefaults = () => {
    return mergeThemeColors(brandColors);
  };

  // For change-detection ONLY. We compare the three user-editable slots
  // with defaults filled in. We can't use `getThemeColorsWithDefaults()`
  // on both sides because the wizard's brandColors has ~80 keys (after
  // BrandColors spreads its merged palette on every change) while the
  // backend stores only 3 keys — JSON.stringify of those two will never
  // be equal, which would make hasChanges true on every open.
  const pickEditableColors = (
    colors?: Partial<ThemeColorPalette> | null,
  ) => ({
    PRIMARY: colors?.PRIMARY || DEFAULT_THEME_COLORS.PRIMARY,
    SECONDARY: colors?.SECONDARY || DEFAULT_THEME_COLORS.SECONDARY,
    ACCENT: colors?.ACCENT || DEFAULT_THEME_COLORS.ACCENT,
  });

  // Version helper: increment the last numeric segment of a version string
  const incrementVersion = (version: string): string => {
    if (!version || version.trim() === '') return '1';
    const parts = version.trim().split('.');
    const lastIdx = parts.length - 1;
    const lastNum = parseInt(parts[lastIdx], 10);
    if (isNaN(lastNum)) return version;
    parts[lastIdx] = String(lastNum + 1);
    return parts.join('.');
  };

  // Version helper: compare two version strings semantically
  // Returns negative if a < b, 0 if equal, positive if a > b
  const compareVersions = (a: string, b: string): number => {
    const aParts = (a || '0').trim().split('.').map(p => parseInt(p, 10) || 0);
    const bParts = (b || '0').trim().split('.').map(p => parseInt(p, 10) || 0);
    const maxLen = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < maxLen; i++) {
      const diff = (aParts[i] || 0) - (bParts[i] || 0);
      if (diff !== 0) return diff;
    }
    return 0;
  };

  // Validate version fields: format rules + version upgrade check (both create and edit modes).
  // In create mode, DB baselines are '0'/'0.0' so the default values ('1'/'1.0') pass automatically.
  const validateVersionInputs = (): boolean => {
    const platform = getValues('platform');
    const newErrors: { androidVersionCode?: string; androidVersionName?: string; iosVersionCode?: string } = {};
    let isValid = true;

    if (platform === 'android' || platform === 'both') {
      // AndroidVersionCode: pure integer only, no dots
      if (!/^\d+$/.test(androidVersionCode.trim())) {
        newErrors.androidVersionCode = 'Only numeric values are allowed. Dot (.) is not permitted.';
        isValid = false;
      } else {
        const cmp = compareVersions(androidVersionCode, dbAndroidVersionCode);
        if (cmp <= 0) {
          newErrors.androidVersionCode = 'Version downgrade is not allowed.';
          isValid = false;
        }
      }

      // AndroidVersionName: 1–2 dots required
      const dotCountName = (androidVersionName.match(/\./g) || []).length;
      if (dotCountName < 1 || dotCountName > 2) {
        newErrors.androidVersionName = 'Must contain at least one dot (.) and a maximum of two dots.';
        isValid = false;
      } else {
        const cmp = compareVersions(androidVersionName, dbAndroidVersionName);
        if (cmp < 0) {
          newErrors.androidVersionName = 'Version downgrade is not allowed.';
          isValid = false;
        }
      }
    }

    if (platform === 'ios' || platform === 'both') {
      // iOSVersionCode: 1–2 dots required
      const dotCountIos = (iosVersionCode.match(/\./g) || []).length;
      if (dotCountIos < 1 || dotCountIos > 2) {
        newErrors.iosVersionCode = 'Must contain at least one dot (.) and a maximum of two dots.';
        isValid = false;
      } else {
        const cmp = compareVersions(iosVersionCode, dbIosVersionCode);
        if (cmp < 0) {
          newErrors.iosVersionCode = 'Version downgrade is not allowed.';
          isValid = false;
        }
      }
    }

    setVersionErrors(newErrors);
    return isValid;
  };

  // Reset form when modal opens/closes (only for create mode)
  useEffect(() => {
    if (isOpen && !isEditMode) {
      reset();
      setCurrentStep(1);
      setCompletedSteps([]);
      setPackageNameStatus('idle');
      setPackageNameError(null);
      setAppIconPreview(null);
      setBrandColors({});
      setHomeBgPreview(null);
      setOnboardBgPreview(null);
      setBuildId(null);
      setBuildStatus('pending');
      
      // Set form open time to prevent false positive change detection
      (window as any).formOpenTime = Date.now();
      
      // Reset change state after a short delay
      setTimeout(() => {
        setHasChanges(false);
      }, 100);
    } else if (!isOpen) {
      // Reset state when modal closes
      setHasChanges(false);
      setIsFormPopulated(false);
    }
  }, [isOpen, isEditMode, reset]);

  // Watch package name for validation (only in create mode)
  const watchedPackageName = watch('packageName');
  const selectedPlatform = watch('platform');
  const mobileProvision = watch('mobileProvision') as File | undefined;
  const mobileProvisionName = watch('mobileProvisionName') as string | undefined;
  const mobileProvisionUUID = watch('mobileProvisionUUID') as string | undefined;
  const showAndroidFields = selectedPlatform === 'android' || selectedPlatform === 'both';
  const showIosFields = selectedPlatform === 'ios' || selectedPlatform === 'both';
  // only show the download button when editing and the app supports android
  const showDownloadButton = isEditMode && (selectedPlatform === 'android' || selectedPlatform === 'both');
  const showIosBuildStatusButton = isEditMode && (selectedPlatform === 'ios' || selectedPlatform === 'both');

  const parseMobileProvisionProfile = useCallback(
    (file: File): Promise<{ name: string; uuid: string } | null> => {
      return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = () => {
          try {
            let content: string;
            if (typeof reader.result === 'string') {
              content = reader.result;
            } else if (reader.result instanceof ArrayBuffer) {
              content = new TextDecoder('utf-8').decode(reader.result);
            } else {
              toast.error('Unable to read provisioning profile content');
              resolve(null);
              return;
            }

            const trimmedName = file.name.replace(/\.mobileprovision$/i, '');

            const uuidMatch = content.match(/<key>UUID<\/key>\s*<string>([^<]+)<\/string>/);
            if (!uuidMatch) {
              toast.error('Could not extract UUID from provisioning profile');
              resolve(null);
              return;
            }

            const uuid = uuidMatch[1].trim();

            setValue('mobileProvisionName', trimmedName);
            setValue('mobileProvisionUUID', uuid);

            toast.success('Provisioning profile parsed successfully');
            resolve({ name: trimmedName, uuid });
          } catch (err) {
            toast.error('Failed to parse provisioning profile');
            resolve(null);
          } finally {
            setIsParsingProvisionProfile(false);
          }
        };

        reader.onerror = () => {
          toast.error('Unable to read provisioning profile');
          setIsParsingProvisionProfile(false);
          resolve(null);
        };

        setIsParsingProvisionProfile(true);
        reader.readAsText(file);
      });
    },
    [setValue],
  );

  const handleMobileProvisionFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.mobileprovision')) {
      toast.error('Please upload a valid .mobileprovision file');
      return;
    }

    setValue('mobileProvision', file);
    await parseMobileProvisionProfile(file);
  };

  const handleRegenerateFromExistingProvision = async () => {
    if (!mobileProvision) {
      toast.error('No provisioning profile file available to regenerate from');
      return;
    }
    await parseMobileProvisionProfile(mobileProvision);
  };

  // Update buildMode when platform changes or when entering step 4 in edit mode
  useEffect(() => {
    if (isEditMode && selectedPlatform && currentStep === 4) {
      // Only update if buildMode doesn't match the platform
      if (selectedPlatform === 'android') {
        // Only show Android options, default to build-only
        if (buildMode === 'ios-build-only') {
          setBuildMode('android-build-only');
        }
      } else if (selectedPlatform === 'ios') {
        // Only show iOS option
        if (buildMode === 'android-build-only' || buildMode === 'android-build-upload') {
          setBuildMode('ios-build-only');
        }
      }
      // For 'both' platform, keep current buildMode (can be any of the three)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, isEditMode, currentStep]);
  useEffect(() => {
    // Skip package name validation in edit mode
    if (isEditMode) {
      setPackageNameStatus('available'); // Set as available since it's already taken by this app
      setPackageNameError(null);
      return;
    }

    const checkPackageName = async () => {
      if (!watchedPackageName || watchedPackageName.length < 3) {
        setPackageNameStatus('idle');
        setPackageNameError(null);
        return;
      }

      setPackageNameStatus('checking');
      setPackageNameError(null);
      
      try {
        // TODO: Implement package name availability check
        const isAvailable = await apiService.checkPackageNameAvailability(watchedPackageName);
        setPackageNameStatus(isAvailable ? 'available' : 'taken');
      } catch (error: any) {
        if (error.message && error.message.includes('format')) {
          setPackageNameStatus('invalid');
          setPackageNameError(error.message);
        } else {
          setPackageNameStatus('taken');
          setPackageNameError('Unable to check package name availability');
        }
      }
    };

    if (watchedPackageName) {
      const timeoutId = setTimeout(checkPackageName, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [watchedPackageName, isEditMode]);

  const getPackageNameMessage = () => {
    if (isEditMode) {
      return 'Package name cannot be changed during edit';
    }
    
    if (!watchedPackageName) return null;
    
    switch (packageNameStatus) {
      case 'checking':
        return 'Checking availability...';
      case 'available':
        return 'Package name is available!';
      case 'taken':
        return 'Package name is already taken';
      case 'invalid':
        return packageNameError || 'Invalid package name format';
      default:
        return null;
    }
  };

  const validateCurrentStep = async (): Promise<boolean> => {
    switch (currentStep) {
      case 1: {
        // Validate name and platform-specific fields
        const formData = getValues();
        let allValid = await trigger('name');

        // Package Name is required for all platforms
        allValid = allValid && await trigger('packageName');

        // Check if app icon is uploaded (either as file or has preview)
        const hasAppIcon = formData.appIcon || appIconPreview;
        if (!hasAppIcon) {
          return false;
        }
        allValid = allValid && await trigger('appIcon');

        // Pipeline ID validation (create mode only — fields are hidden in edit mode)
        if (!isEditMode) {
          const platform = formData.platform;
          if (platform === 'android' || platform === 'both') {
            allValid = allValid && await trigger('pipelineId');
          }
          if (platform === 'ios' || platform === 'both') {
            allValid = allValid && await trigger('iosPipelineId');
          }
        }

        // Version field validation (format + upgrade check)
        const versionValid = validateVersionInputs();
        if (!versionValid) {
          allValid = false;
        }

        return allValid;
      }
      case 2:
        return true; // Theme step doesn't require validation
      case 3:
        // Check platform-specific requirements
        const step3FormData = getValues();
        const platform = step3FormData.platform;
        let step3Valid = true;
        
        // For Android or Both platforms, check SHA fingerprints
        if (platform === 'android' || platform === 'both') {
          const hasSha1 = step3FormData.sha1 && step3FormData.sha1.trim() !== '';
          const hasSha256 = step3FormData.sha256 && step3FormData.sha256.trim() !== '';
          
          if (!hasSha1 || !hasSha256) {
            toast.error('Please generate SHA1 and SHA256 fingerprints before proceeding');
            return false;
          }
        }
        
        // For Android or Both platforms, check Google Services JSON
        if (platform === 'android' || platform === 'both') {
          const hasGoogleServicesJson = step3FormData.googleServicesJson || googleServicesJsonPreview;
          if (!hasGoogleServicesJson) {
            toast.error('Please upload Google Services JSON file for Android');
            return false;
          }
        }
        
        // For iOS or Both platforms, check Google Services Plist
        if (platform === 'ios' || platform === 'both') {
          const hasGoogleServicesPlist = step3FormData.googleServicesPlist || googleServicesPlistPreview;
          if (!hasGoogleServicesPlist) {
            toast.error('Please upload Google Services Info.plist file for iOS');
            return false;
          }

          // For iOS or Both platforms, ensure provisioning profile metadata is available
          const hasProvisionProfileName =
            step3FormData.mobileProvisionName && step3FormData.mobileProvisionName.trim() !== '';
          const hasProvisionProfileUUID =
            step3FormData.mobileProvisionUUID && step3FormData.mobileProvisionUUID.trim() !== '';

          if (!hasProvisionProfileName || !hasProvisionProfileUUID) {
            toast.error('Please upload and parse an iOS provisioning profile to extract Name and UUID');
            return false;
          }
        }
        
        // Validate Google Client API fields (only in create mode, not edit mode)
        if (!isEditMode) {
          // For Android or Both platforms, validate Google Client API (Android)
          if (platform === 'android' || platform === 'both') {
            const googleClientApiAndroidValid = await trigger('googleClientApiAndroid');
            if (!googleClientApiAndroidValid) {
              toast.error('Please provide Google Client API (Android)');
              step3Valid = false;
            }
          }
          
          // For iOS or Both platforms, validate Google Client API (iOS)
          if (platform === 'ios' || platform === 'both') {
            const googleClientApiIosValid = await trigger('googleClientApiIos');
            if (!googleClientApiIosValid) {
              toast.error('Please provide Google Client API (iOS)');
              step3Valid = false;
            }
          }
        }
        
        return step3Valid;
      case 4:
        // Step 4 is optional - just return true to allow submission
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) return;

    if (currentStep < 4) {
      setCompletedSteps(prev => {
        const updatedSet = new Set(prev);
        updatedSet.add(currentStep);
        return Array.from(updatedSet).sort((a, b) => a - b);
      });
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setCompletedSteps(prev => prev.filter(step => step < newStep));
      setCurrentStep(newStep);
    }
  };

  const handleStepClick = async (stepNumber: number) => {
    // Prevent jumping to step 4 if step 3 is not completed
    if (stepNumber === 4) {
      const formData = getValues();
      const hasSha1 = formData.sha1 && formData.sha1.trim() !== '';
      const hasSha256 = formData.sha256 && formData.sha256.trim() !== '';
      
      if (!hasSha1 || !hasSha256) {
        toast.error('Please complete step 3 (Firebase Configuration) before proceeding to step 4');
        return;
      }
    }
    
    if (stepNumber <= currentStep || completedSteps.includes(stepNumber - 1)) {
      if (stepNumber < currentStep) {
        // Going backwards - remove all steps after the clicked step
        setCompletedSteps(prev => prev.filter(step => step < stepNumber));
      } else if (stepNumber > currentStep) {
        // Going forwards - ensure all previous steps are marked as completed
        setCompletedSteps(prev => {
          const requiredSteps = Array.from({ length: stepNumber - 1 }, (_, i) => i + 1);
          const updatedSet = new Set([...prev, ...requiredSteps]);
          return Array.from(updatedSet).sort((a, b) => a - b);
        });
      }
      setCurrentStep(stepNumber);
    }
  };

  const handleFormSubmit = async (data: AppCreationFormDataExtended) => {
    // Validate essential fields before submission
    const isStep3Valid = await validateCurrentStep();
    if (!isStep3Valid && currentStep === 3) {
      toast.error('Please complete all required fields in Firebase Configuration');
      return;
    }

    // Clean platform value - handle comma-separated strings and arrays
    let cleanPlatform: 'android' | 'ios' | 'both' = data.platform || 'both';
    if (Array.isArray(cleanPlatform)) {
      cleanPlatform = cleanPlatform[0] as 'android' | 'ios' | 'both';
    } else if (typeof cleanPlatform === 'string' && cleanPlatform.includes(',')) {
      // Handle comma-separated strings like "android,android"
      cleanPlatform = cleanPlatform.split(',')[0].trim() as 'android' | 'ios' | 'both';
    }

    // Validate platform value
    if (!['android', 'ios', 'both'].includes(cleanPlatform)) {
      cleanPlatform = 'both'; // fallback
    }

    const finalData: AppCreationFormData = {
      ...data,
      platform: cleanPlatform,
      themeColors: {
        PRIMARY: brandColors.PRIMARY || DEFAULT_THEME_COLORS.PRIMARY,
        SECONDARY: brandColors.SECONDARY || DEFAULT_THEME_COLORS.SECONDARY,
        ACCENT: brandColors.ACCENT || DEFAULT_THEME_COLORS.ACCENT,
      },
      buildId: buildId || undefined,
      buildStatus,
      iosPipelineId: data.iosPipelineId || editData?.iosPipelineId,
      iosBuildId: data.iosBuildId || editData?.iosBuildId,
      iosBuildStatus: data.iosBuildStatus ?? editData?.iosBuildStatus,
      // Set stepsDone to current step for final submission, or highest completed step for drafts
      stepsDone: currentStep.toString(),
      // Include pipelineId if provided
      pipelineId: data.pipelineId || editData?.pipelineId,
      // Include background images (for both create and edit modes)
      onboardBg: data.onboardBg,
      homeBg: data.homeBg,
      // Include keystore data if generated (including buffer for upload)
      ...(keystoreData && {
        keystoreSha1: keystoreData.keystoreSha1,
        keystoreSha256: keystoreData.keystoreSha256,
        keystoreBuffer: keystoreData.keystoreBuffer, // Base64 buffer for upload
        keystorePassword: keystoreData.keystorePassword,
        keystoreAlias: keystoreData.keystoreAlias,
      }),
      // Include version data from local state
      androidVersionCode,
      androidVersionName,
      iosVersionCode,
    };

    try {
      await onSubmit(finalData);

      // After creation, try to find the created flavour and trigger build
      try {
        const flavours = await apiService.getAvailableFlavours();
        const created = flavours.find(
          (f) =>
            f.packageName === finalData.packageName &&
            f.name === finalData.name,
        );
        if (created?.id) {
          apiService.setFlavourId(String(created.id));
          localStorage.setItem("flavourId", String(created.id));
          if (currentStep === 4) {
            const successfulPlatforms: string[] = [];

            if (isEditMode) {
              // Edit Mode: Run only the selected build mode's pipeline
              try {
                if (buildMode === 'android-build-only' || buildMode === 'android-build-upload') {
                  // Run Android pipeline only
                  const pipelineIdForPlatform = finalData.pipelineId || created.pipelineId;
                  
                  if (!pipelineIdForPlatform) {
                    throw new Error('Android pipeline ID is required');
                  }

                  // Determine androidPlayUpload based on buildMode
                  const androidPlayUpload = buildMode === 'android-build-upload';

                  // Update pipeline variables
                  await apiService.updatePipelineVariables(
                    pipelineIdForPlatform,
                    finalData.packageName,
                    androidPlayUpload,
                    'android'
                  );

                  // Trigger Android build
                  await apiService.triggerBuildWithPipeline(
                    String(created.id),
                    pipelineIdForPlatform,
                    'android',
                  );
                  
                  successfulPlatforms.push('Android');
                } else if (buildMode === 'ios-build-only') {
                  // Run iOS pipeline only
                  const pipelineIdForPlatform = finalData.iosPipelineId || created.iosPipelineId;
                  
                  if (!pipelineIdForPlatform) {
                    throw new Error('iOS pipeline ID is required');
                  }

                  // Update pipeline variables for iOS (use packageName)
                  await apiService.updatePipelineVariables(
                    pipelineIdForPlatform,
                    finalData.packageName,
                    undefined, // androidPlayUpload not needed for iOS
                    'ios'
                  );

                  // Trigger iOS build
                  await apiService.triggerBuildWithPipeline(
                    String(created.id),
                    pipelineIdForPlatform,
                    'ios',
                  );
                  
                  successfulPlatforms.push('iOS');
                }
              } catch (buildError: any) {
                toast.error(
                  buildError?.message ||
                    'There was an error triggering the build. Please try again.',
                );
              }
            } else {
              // Create Mode: Run all platforms based on selected platform
              const platformsToTrigger: ('android' | 'ios')[] = [];
              if (finalData.platform === 'android' || finalData.platform === 'both') {
                platformsToTrigger.push('android');
              }
              if (finalData.platform === 'ios' || finalData.platform === 'both') {
                platformsToTrigger.push('ios');
              }

              for (const platform of platformsToTrigger) {
                try {
                  const pipelineIdForPlatform =
                    platform === 'ios'
                      ? finalData.iosPipelineId || created.iosPipelineId
                      : finalData.pipelineId || created.pipelineId;

                  if (pipelineIdForPlatform) {
                    // For create mode, androidPlayUpload defaults to false
                    const androidPlayUpload = platform === 'android' ? false : undefined;
                    const packageNameForPipeline = finalData.packageName;

                    // Update pipeline variables
                    await apiService.updatePipelineVariables(
                      pipelineIdForPlatform,
                      packageNameForPipeline,
                      androidPlayUpload,
                      platform
                    );

                    // Trigger build after updating variables
                    await apiService.triggerBuildWithPipeline(
                      String(created.id),
                      pipelineIdForPlatform,
                      platform,
                    );
                  } else {
                    await apiService.triggerBuild(String(created.id), platform);
                  }
                  successfulPlatforms.push(platform === 'ios' ? 'iOS' : 'Android');
                } catch (buildError: any) {
                  toast.error(
                    buildError?.message ||
                      `There was an error triggering the ${platform === 'ios' ? 'iOS' : 'Android'} build. Please try again.`,
                  );
                }
              }
            }

            if (successfulPlatforms.length > 0) {
              toast.success(`${successfulPlatforms.join(' & ')} build triggered for the app.`);
            }
          }
        }
      } catch (e) {
        // ignore auto-trigger build failure
      }

      onClose();
    } catch (error) {
      // The parent (Apps.tsx) handles success/failure toasts in its own
      // onSubmit callback (onSubmitWizard for create, onSubmitEdit for edit).
      // We deliberately do NOT toast here to avoid duplicate / wrong-mode
      // messages (e.g. an "App created successfully!" toast firing on an
      // edit-mode submit). If we hit this catch the parent has already
      // shown its appropriate error toast; we just refrain from closing
      // the modal.
    }
  };

  const handleSaveDraft = async () => {
    // Get current form data without validation
    const formData = getValues();

    // Clean platform value - handle comma-separated strings and arrays
    let cleanPlatform: 'android' | 'ios' | 'both' = formData.platform || 'both';
    if (Array.isArray(cleanPlatform)) {
      cleanPlatform = cleanPlatform[0] as 'android' | 'ios' | 'both';
    } else if (typeof cleanPlatform === 'string' && cleanPlatform.includes(',')) {
      // Handle comma-separated strings like "android,android"
      cleanPlatform = cleanPlatform.split(',')[0].trim() as 'android' | 'ios' | 'both';
    }

    // Validate platform value
    if (!['android', 'ios', 'both'].includes(cleanPlatform)) {
      cleanPlatform = 'both'; // fallback
    }

    const finalData: AppCreationFormData = {
      ...formData,
      platform: cleanPlatform,
      themeColors: {
        PRIMARY: brandColors.PRIMARY || DEFAULT_THEME_COLORS.PRIMARY,
        SECONDARY: brandColors.SECONDARY || DEFAULT_THEME_COLORS.SECONDARY,
        ACCENT: brandColors.ACCENT || DEFAULT_THEME_COLORS.ACCENT,
      },
      buildId: buildId || undefined,
      buildStatus: 'pending',
      iosPipelineId: formData.iosPipelineId || editData?.iosPipelineId,
      iosBuildId: formData.iosBuildId || editData?.iosBuildId,
      iosBuildStatus: formData.iosBuildStatus ?? editData?.iosBuildStatus,
      // Set stepsDone to current step for draft saves
      stepsDone: currentStep.toString(),
      // Include pipelineId if provided
      pipelineId: formData.pipelineId || editData?.pipelineId,
      // Include background images (for both create and edit modes)
      onboardBg: formData.onboardBg,
      homeBg: formData.homeBg,
      // Include keystore data if generated (including buffer for upload)
      ...(keystoreData && {
        keystoreSha1: keystoreData.keystoreSha1,
        keystoreSha256: keystoreData.keystoreSha256,
        keystoreBuffer: keystoreData.keystoreBuffer, // Base64 buffer for upload
        keystorePassword: keystoreData.keystorePassword,
        keystoreAlias: keystoreData.keystoreAlias,
      }),
      // Include version data from local state
      androidVersionCode,
      androidVersionName,
      iosVersionCode,
    };

    setIsDraftSaving(true);
    try {
      if (onSaveDraft) {
        await onSaveDraft(finalData);

        if (!isEditMode) {
          onClose();
        } else {
          // Save succeeded and we're keeping the popup open — snapshot the
          // current state as the new "original" so hasChanges recomputes to
          // false. That hides the "Save Changes" button (which is gated on
          // hasChanges) until the user modifies something again.
          const snapshot: AppCreationFormDataExtended = {
            ...formData,
            platform: cleanPlatform,
            themeColors: getThemeColorsWithDefaults(),
            appIconUrl: appIconPreview ?? undefined,
            onboardBgUrl: onboardBgPreview ?? undefined,
            homeBgUrl: homeBgPreview ?? undefined,
            googleServicesJsonUrl: googleServicesJsonPreview ?? undefined,
            googleServicesPlistUrl: googleServicesPlistPreview ?? undefined,
          };
          setOriginalData(snapshot);
          (window as any).formPopulationTime = Date.now();
          setHasChanges(false);
        }
      }
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setIsDraftSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Settings className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">Basic Information</h3>
              <p className="text-gray-600">Enter your app's basic details and identification</p>
            </div>

            <div className="space-y-4">
              {/* App Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    App Name *
                  </label>
                  <input
                    {...register('name', { required: 'App name is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Enter app name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Enter app description"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              {/* Target Platform */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Target Platform *
                </label>
                {!isEditMode ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="relative">
                      <input
                        type="radio"
                        value="ios"
                        {...register('platform', { required: 'Please select a platform' })}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        watch('platform') === 'ios' 
                          ? 'border-primary bg-primary-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="text-center">
                          <div className="text-2xl mb-2">🍎</div>
                          <div className="font-medium">iOS Only</div>
                          <div className="text-sm text-gray-500">Apple App Store</div>
                        </div>
                      </div>
                    </label>

                    <label className="relative">
                      <input
                        type="radio"
                        value="android"
                        {...register('platform', { required: 'Please select a platform' })}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        watch('platform') === 'android' 
                          ? 'border-primary bg-primary-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="text-center">
                          <div className="text-2xl mb-2">🤖</div>
                          <div className="font-medium">Android Only</div>
                          <div className="text-sm text-gray-500">Google Play Store</div>
                        </div>
                      </div>
                    </label>

                    <label className="relative">
                      <input
                        type="radio"
                        value="both"
                        {...register('platform', { required: 'Please select a platform' })}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        watch('platform') === 'both' 
                          ? 'border-primary bg-primary-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="text-center">
                          <div className="text-2xl mb-2">📱</div>
                          <div className="font-medium">Both Platforms</div>
                          <div className="text-sm text-gray-500">iOS & Android</div>
                        </div>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input type="hidden" {...register('platform')} />
                    {selectedPlatform === 'ios' && (
                      <div className="p-4 border-2 rounded-lg bg-gray-100 text-center">
                        <div className="text-center">
                          <div className="text-2xl mb-2">🍎</div>
                          <div className="font-medium">iOS Only</div>
                          <div className="text-sm text-gray-500">Apple App Store</div>
                        </div>
                      </div>
                    )}
                    {selectedPlatform === 'android' && (
                      <div className="p-4 border-2 rounded-lg bg-gray-100 text-center">
                        <div className="text-center">
                          <div className="text-2xl mb-2">🤖</div>
                          <div className="font-medium">Android Only</div>
                          <div className="text-sm text-gray-500">Google Play Store</div>
                        </div>
                      </div>
                    )}
                    {selectedPlatform === 'both' && (
                      <div className="p-4 border-2 rounded-lg bg-gray-100 text-center">
                        <div className="text-center">
                          <div className="text-2xl mb-2">📱</div>
                          <div className="font-medium">Both Platforms</div>
                          <div className="text-sm text-gray-500">iOS & Android</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {errors.platform && (
                  <p className="mt-2 text-sm text-red-600">{errors.platform.message}</p>
                )}
                
                <p className="mt-3 text-xs text-gray-500">
                  {watch('platform') === 'android' && 'You will need to provide a Package Name'}
                  {watch('platform') === 'ios' && 'You will need to provide a Package Name'}
                  {watch('platform') === 'both' && 'You will need to provide a Package Name'}
                </p>
              </div>

              {/* Package Name - Required for all platforms */}
              {selectedPlatform && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const platformValue = selectedPlatform;
                    const isDisabled = isEditMode && !!originalPackageName;
                    return (
                      <PackageNameInput
                        register={register as any}
                        error={errors.packageName}
                        packageNameStatus={packageNameStatus}
                        packageNameMessage={getPackageNameMessage()}
                        disabled={isDisabled}
                        platform={platformValue}
                      />
                    );
                  })()}
                </div>
              )}

              {/* Pipeline IDs (create mode only) */}
              {!isEditMode && selectedPlatform && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {showAndroidFields && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Android Pipeline ID <span className="text-red-600">*</span>
                      </label>
                      <input
                        {...register('pipelineId', {
                          validate: (value) => {
                            const platform = getValues('platform');
                            if (platform === 'android' || platform === 'both') {
                              return value && value.trim() !== ''
                                ? true
                                : 'Android pipeline ID is required.';
                            }
                            return true;
                          },
                        })}
                        type="text"
                        className={`w-full px-3 py-2 border ${
                          errors.pipelineId ? 'border-red-500' : 'border-gray-300'
                        } rounded-md focus:outline-none focus:ring-primary focus:border-primary`}
                        placeholder="Azure DevOps pipeline ID for Android"
                      />
                      {errors.pipelineId && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.pipelineId.message as string}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        We use this ID to trigger the Android build pipeline automatically.
                      </p>
                    </div>
                  )}

                  {showIosFields && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        iOS Pipeline ID <span className="text-red-600">*</span>
                      </label>
                      <input
                        {...register('iosPipelineId', {
                          validate: (value) => {
                            const platform = getValues('platform');
                            if (platform === 'ios' || platform === 'both') {
                              return value && value.trim() !== ''
                                ? true
                                : 'iOS pipeline ID is required.';
                            }
                            return true;
                          },
                        })}
                        type="text"
                        className={`w-full px-3 py-2 border ${
                          errors.iosPipelineId ? 'border-red-500' : 'border-gray-300'
                        } rounded-md focus:outline-none focus:ring-primary focus:border-primary`}
                        placeholder="Azure DevOps pipeline ID for iOS"
                      />
                      {errors.iosPipelineId && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.iosPipelineId.message as string}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        We use this ID to trigger the iOS build pipeline automatically.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <AppIconUploader
                  register={register as any}
                  preview={appIconPreview}
                  onPreviewChange={setAppIconPreview}
                  setValue={setValue as any}
                  trigger={trigger}
                  inputId="app-icon-upload-wizard"
                  isRequired={true}
                  error={errors.appIcon}
                />
              </div>

              {/* iOS Provisioning Profile Upload (Step 1, iOS/Both only, styled like app icon upload) */}
              {showIosFields && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    iOS Provisioning Profile
                  </label>

                  <div className="flex items-center space-x-3">
                        {/* Upload button */}
                        <div>
                          <input
                            type="file"
                            accept=".mobileprovision"
                            onChange={handleMobileProvisionFile}
                            className="hidden"
                            id="ios-mobile-provision-step1"
                          />
                          <label
                            htmlFor="ios-mobile-provision-step1"
                            className="cursor-pointer inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary border-gray-300 text-gray-700"
                          >
                            {isParsingProvisionProfile ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                {(mobileProvision || mobileProvisionName) ? 'Change Provisioning Profile' : 'Upload Provisioning Profile'}
                              </>
                            )}
                          </label>
                        </div>

                        {/* Regenerate button, directly after upload */}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRegenerateFromExistingProvision}
                          disabled={!mobileProvision || isParsingProvisionProfile}
                        >
                          {isParsingProvisionProfile ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              Regenerating...
                            </>
                          ) : (
                            'Regenerate'
                          )}
                        </Button>
                      </div>

                      <p className="text-xs text-gray-500 mt-1">
                        Only .mobileprovision file are supported.
                      </p>

                      {(mobileProvisionName || mobileProvisionUUID) && (
                        <div className="mt-2 flex flex-col md:flex-row md:items-center md:justify-between md:space-x-4 space-y-2 md:space-y-0">
                          <div className="text-xs text-gray-700 space-y-1">
                            <p>
                              <span className="font-medium">Profile Name:</span>{' '}
                              {mobileProvisionName || 'Not extracted'}
                            </p>
                            <p>
                              <span className="font-medium">UUID:</span>{' '}
                              {mobileProvisionUUID || 'Not extracted'}
                            </p>
                          </div>
                        </div>
                      )}
                </div>
              )}

              {/* Version Fields — conditionally shown based on platform */}
              {selectedPlatform && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    App Version
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Android Version Code — shown for android & both */}
                    {(selectedPlatform === 'android' || selectedPlatform === 'both') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Android Version Code
                        </label>
                        <input
                          type="text"
                          value={androidVersionCode}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAndroidVersionCode(val);
                            if (!/^\d+$/.test(val.trim())) {
                              setVersionErrors(prev => ({ ...prev, androidVersionCode: 'Only numeric values are allowed. Dot (.) is not permitted.' }));
                            } else {
                              const cmp = compareVersions(val, dbAndroidVersionCode);
                              if (cmp === 0) {
                                setVersionErrors(prev => ({ ...prev, androidVersionCode: 'Version must be higher than the current existing version.' }));
                              } else if (cmp < 0) {
                                setVersionErrors(prev => ({ ...prev, androidVersionCode: 'Version downgrade is not allowed.' }));
                              } else {
                                setVersionErrors(prev => ({ ...prev, androidVersionCode: undefined }));
                              }
                            }
                          }}
                          className={`w-full px-3 py-2 border ${versionErrors.androidVersionCode ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm`}
                          placeholder="e.g. 1"
                        />
                        {versionErrors.androidVersionCode && (
                          <p className="mt-1 text-xs text-red-600">{versionErrors.androidVersionCode}</p>
                        )}
                        {isEditMode && (
                          <p className="mt-1 text-xs text-gray-400">Current existing version: {dbAndroidVersionCode}</p>
                        )}
                      </div>
                    )}

                    {/* Android Version Name — shown for android & both */}
                    {(selectedPlatform === 'android' || selectedPlatform === 'both') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Android Version Name
                        </label>
                        <input
                          type="text"
                          value={androidVersionName}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAndroidVersionName(val);
                            const dotCount = (val.match(/\./g) || []).length;
                            if (dotCount < 1 || dotCount > 2) {
                              setVersionErrors(prev => ({ ...prev, androidVersionName: 'Must contain at least one dot (.) and a maximum of two dots.' }));
                            } else {
                              const cmp = compareVersions(val, dbAndroidVersionName);
                              if (cmp < 0) {
                                setVersionErrors(prev => ({ ...prev, androidVersionName: 'Version downgrade is not allowed.' }));
                              } else {
                                setVersionErrors(prev => ({ ...prev, androidVersionName: undefined }));
                              }
                            }
                          }}
                          className={`w-full px-3 py-2 border ${versionErrors.androidVersionName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm`}
                          placeholder="e.g. 1.0"
                        />
                        {versionErrors.androidVersionName && (
                          <p className="mt-1 text-xs text-red-600">{versionErrors.androidVersionName}</p>
                        )}
                        {isEditMode && (
                          <p className="mt-1 text-xs text-gray-400">Current existing version: {dbAndroidVersionName}</p>
                        )}
                      </div>
                    )}

                    {/* iOS Version Code — shown for ios & both */}
                    {(selectedPlatform === 'ios' || selectedPlatform === 'both') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          iOS Version Code
                        </label>
                        <input
                          type="text"
                          value={iosVersionCode}
                          onChange={(e) => {
                            const val = e.target.value;
                            setIosVersionCode(val);
                            const dotCount = (val.match(/\./g) || []).length;
                            if (dotCount < 1 || dotCount > 2) {
                              setVersionErrors(prev => ({ ...prev, iosVersionCode: 'Must contain at least one dot (.) and a maximum of two dots.' }));
                            } else {
                              const cmp = compareVersions(val, dbIosVersionCode);
                              if (cmp < 0) {
                                setVersionErrors(prev => ({ ...prev, iosVersionCode: 'Version downgrade is not allowed.' }));
                              } else {
                                setVersionErrors(prev => ({ ...prev, iosVersionCode: undefined }));
                              }
                            }
                          }}
                          className={`w-full px-3 py-2 border ${versionErrors.iosVersionCode ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm`}
                          placeholder="e.g. 1.0"
                        />
                        {versionErrors.iosVersionCode && (
                          <p className="mt-1 text-xs text-red-600">{versionErrors.iosVersionCode}</p>
                        )}
                        {isEditMode && (
                          <p className="mt-1 text-xs text-gray-400">Current existing version: {dbIosVersionCode}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Palette className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">Theme & Preview</h3>
              <p className="text-gray-600">Customize your app's appearance and see a live preview</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-4">
                <BrandColors
                  value={brandColors}
                  onChange={setBrandColors}
                  homeBgPreview={homeBgPreview}
                  onboardBgPreview={onboardBgPreview}
                  onHomeBgChange={setHomeBgPreview}
                  onOnboardBgChange={setOnboardBgPreview}
                  setValue={setValue}
                />
              </div>
              
              <div className="flex justify-center">
                <MobilePreview
                  appName={watch('name') || 'Your App'}
                  appIcon={appIconPreview}
                  themeColors={getThemeColorsWithDefaults()}
                  homeBgPreview={homeBgPreview}
                  onboardBgPreview={onboardBgPreview}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Cloud className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">Firebase Configuration</h3>
              <p className="text-gray-600">Configure Firebase and upload certificates</p>
            </div>

            <FirebaseConfigStep
              register={register as any}
              setValue={setValue as any}
              watch={watch as any}
              errors={errors as any}
              onKeystoreGenerated={setKeystoreData}
              googleServicesJsonPreview={googleServicesJsonPreview}
              googleServicesPlistPreview={googleServicesPlistPreview}
              onGoogleServicesJsonPreviewChange={setGoogleServicesJsonPreview}
              onGoogleServicesPlistPreviewChange={setGoogleServicesPlistPreview}
              isEditMode={isEditMode}
              editData={editData}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">Review & Create</h3>
              <p className="text-gray-600">Review your app configuration and create your app</p>
            </div>

            {/* App Configuration Summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">App Configuration Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-gray-700 mb-3">Basic Information</h5>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><span className="font-medium">Name:</span> {watch('name') || 'Not set'}</p>
                    <p><span className="font-medium">Package Name:</span> {watch('packageName') || 'Not set'}</p>
                    <p><span className="font-medium">Platform:</span> {watch('platform') || 'Not set'}</p>
                    {/* Version fields */}
                    {(['android', 'both'].includes(watch('platform') || '')) && (
                      <>
                        <p><span className="font-medium">Android Version Code:</span> {androidVersionCode || 'Not set'}</p>
                        <p><span className="font-medium">Android Version Name:</span> {androidVersionName || 'Not set'}</p>
                      </>
                    )}
                    {(['ios', 'both'].includes(watch('platform') || '')) && (
                      <p><span className="font-medium">iOS Version Code:</span> {iosVersionCode || 'Not set'}</p>
                    )}
                  </div>
                </div>
                <div>
                  <h5 className="font-medium text-gray-700 mb-3">Security & Configuration</h5>
                  <div className="space-y-2 text-sm text-gray-600">
                     {/* Pipeline ID */}
                        <p>
                          <span className="font-medium">Pipeline ID:</span>{" "}
                          {watch("pipelineId") || "Not set"}
                        </p>
                        {(['ios', 'both'].includes(watch('platform') || '')) && (
                          <p>
                            <span className="font-medium">iOS Pipeline ID:</span>{' '}
                            {watch('iosPipelineId') || 'Not set'}
                          </p>
                        )}
                        
                    {/* SHA fingerprints - only show for Android/Both platforms */}
                    {(['android', 'both'].includes(watch('platform') || '')) && (
                      <>
                        <p><span className="font-medium">SHA1:</span> {watch('sha1') ? '✅ Generated' : '❌ Not generated'}</p>
                        <p><span className="font-medium">SHA256:</span> {watch('sha256') ? '✅ Generated' : '❌ Not generated'}</p>
                      </>
                    )}
                    
                    {/* Firebase Android - only show for Android/Both platforms */}
                    {(['android', 'both'].includes(watch('platform') || '')) && (
                      <p><span className="font-medium">Firebase Android:</span> {
                        (watch('googleServicesJson') || googleServicesJsonPreview) ? '✅ Configured' : '❌ Not configured'
                      }</p>
                    )}
                    
                    {/* Firebase iOS - only show for iOS/Both platforms */}
                    {(['ios', 'both'].includes(watch('platform') || '')) && (
                      <p><span className="font-medium">Firebase iOS:</span> {
                        (watch('googleServicesPlist') || googleServicesPlistPreview) ? '✅ Configured' : '❌ Not configured'
                      }</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-green-900">
                    {isEditMode ? 'Ready to Update' : 'Ready to Create'}
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    {isEditMode 
                      ? 'Your app configuration is ready to be updated. Click "Submit" to save your changes including any new settings.'
                      : 'Your app configuration is complete. Click "Submit" to create your app with all the configured settings including keystore generation and Firebase integration.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };
  
  const handleDownloadAab = async () => {
    if (!editId) {
      toast.error('App ID not found');
      return;
    }

    setIsDownloading(true);
    try {
      await apiService.downloadAab(editId);
      toast.success('.aab file downloaded successfully!');
    } catch (error: any) {
      // Handle specific error messages from backend
      if (error.message?.includes('Build is currently in progress')) {
        toast.error('Build is currently in progress. Please wait...');
      } else if (error.message?.includes('Build failed')) {
        toast.error('Build failed. Please try again later.');
      } else {
        toast.error(error.message || 'Failed to download .aab file');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCheckIosBuildStatus = async () => {
    if (!editId) {
      toast.error('App ID not found');
      return;
    }

    setIsCheckingIosBuild(true);
    try {
      const { buildState } = await apiService.checkIosBuildStatus(editId);

      if (buildState === 'success') {
        toast.success('iOS build completed successfully!');
      } else if (buildState === 'failed') {
        toast.error('iOS build failed. Please check the pipeline logs.');
      } else {
        toast('iOS build is currently in progress...', { icon: '🟡' });
      }
    } catch (error: any) {
      toast.error(error.message || 'Unable to fetch build status');
    } finally {
      setIsCheckingIosBuild(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-1 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0 ">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[95vh] overflow-auto">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit App' : 'Create New App'}
            </h2>
            <div className="flex items-center space-x-3">
              {showDownloadButton && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDownloadAab}
                  disabled={isDownloading || isLoading}
                  className="flex items-center space-x-2"
                >
                  {isDownloading  ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      <span>Download Android .aab </span>
                    </>
                  )}
                </Button>
              )}
              {showIosBuildStatusButton && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckIosBuildStatus}
                  disabled={isCheckingIosBuild || isLoading}
                  className="flex items-center space-x-2"
                >
                  {isCheckingIosBuild ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking...</span>
                    </>
                  ) : (
                    <>
                      <Smartphone className="h-4 w-4" />
                      <span>Check iOS Build Status</span>
                    </>
                  )}
                </Button>
              )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          </div>
        </div>
        {/* Compact Stepper */}
        <div className="bg-gray-50 px-6 py-3 border-b">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const isActive = currentStep === step.id;
              const isCompleted = completedSteps.includes(step.id);
              
              // Special logic for step 4 - require step 3 completion
              let isClickable = step.id <= currentStep || completedSteps.includes(step.id - 1);
              if (step.id === 4) {
                const stepperFormData = getValues();
                const hasSha1 = stepperFormData.sha1 && stepperFormData.sha1.trim() !== '';
                const hasSha256 = stepperFormData.sha256 && stepperFormData.sha256.trim() !== '';
                isClickable = isClickable && Boolean(hasSha1 && hasSha256);
              }
              
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => isClickable && handleStepClick(step.id)}
                    disabled={!isClickable}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-primary-600 text-white shadow-md'
                        : isCompleted
                        ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-sm'
                        : isClickable
                        ? 'bg-white text-gray-700 hover:bg-primary-50 hover:text-primary-700 shadow-sm border border-gray-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4 text-white" />
                    ) : (
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                        isActive 
                          ? 'border-white text-white bg-primary-700' 
                          : 'border-current'
                      }`}>
                        {step.id}
                      </div>
                    )}
                    <span className={`text-sm font-medium ${
                      isActive ? 'text-white font-semibold' : ''
                    }`}>
                      {step.title}
                    </span>
                  </button>
                  
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mx-3 transition-colors ${
                      completedSteps.includes(step.id) ? 'bg-primary-400' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Compact progress bar */}
          <div className="mt-3 flex items-center space-x-3">
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-gradient-to-r from-primary-400 to-primary-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((completedSteps.length / steps.length) * 100, 100)}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 font-medium min-w-fit">
              {Math.round(Math.min((completedSteps.length / steps.length) * 100, 100))}% Complete
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[60vh]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-between">
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={currentStep === 1 ? onClose : handlePrevious}
                disabled={isLoading}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                {currentStep === 1 ? 'Cancel' : 'Previous'}
              </Button>
              
              {onSaveDraft && hasChanges && currentStep < 4 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={isLoading || isDraftSaving}
                >
                  {isDraftSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    isEditMode ? 'Save Changes' : 'Save as Draft'
                  )}
                </Button>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {/* Build Mode Dropdown - Only show in edit mode at step 4 */}
              {isEditMode && currentStep === 4 && (
                <select
                  value={buildMode}
                  onChange={(e) => setBuildMode(e.target.value as any)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  {/* Show Android options only if platform is android or both */}
                  {selectedPlatform === 'android' && (
                    <>
                      <option value="android-build-only">Android: Build Only</option>
                      <option value="android-build-upload">Android: Build & Upload</option>
                    </>
                  )}
                  {/* Show iOS option only if platform is ios */}
                  {selectedPlatform === 'ios' && (
                    <option value="ios-build-only">iOS: Build Only</option>
                  )}
                  {/* Show all options if platform is both */}
                  {selectedPlatform === 'both' && (
                    <>
                      <option value="android-build-only">Android: Build Only</option>
                      <option value="android-build-upload">Android: Build & Upload</option>
                      <option value="ios-build-only">iOS: Build Only</option>
                    </>
                  )}
                </select>
              )}

              {currentStep < 4 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={isLoading}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    handleSubmit(handleFormSubmit)();
                  }}
                  loading={isLoading}
                >
                  Submit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppCreationWizard;
